/**
 * Collect JSON paths from a sample payload for source field pickers.
 * Paths use gjson-style `$.` prefix.
 */

const MAX_PATHS = 500;

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function appendSegment(base, segment) {
  if (base === '$') {
    return `$.${segment}`;
  }

  return `${base}.${segment}`;
}

function walk(value, currentPath, paths, seen) {
  if (paths.length >= MAX_PATHS) {
    return;
  }

  if (value === null || value === undefined) {
    if (!seen.has(currentPath)) {
      seen.add(currentPath);
      paths.push(currentPath);
    }

    return;
  }

  if (Array.isArray(value)) {
    if (!seen.has(currentPath)) {
      seen.add(currentPath);
      paths.push(currentPath);
    }

    const limit = Math.min(value.length, 5);
    for (let i = 0; i < limit; i += 1) {
      walk(value[i], appendSegment(currentPath, String(i)), paths, seen);
    }

    return;
  }

  if (isPlainObject(value)) {
    if (!seen.has(currentPath)) {
      seen.add(currentPath);
      paths.push(currentPath);
    }

    Object.keys(value).forEach((key) => {
      walk(value[key], appendSegment(currentPath, key), paths, seen);
    });

    return;
  }

  if (!seen.has(currentPath)) {
    seen.add(currentPath);
    paths.push(currentPath);
  }
}

/**
 * Resolve a simple `$.a.b` path against a parsed JSON value (first array index when omitted).
 */
export function resolveJsonPath(payload, path) {
  if (payload === null || payload === undefined) {
    return undefined;
  }

  if (!path || path === '$') {
    return payload;
  }

  if (!path.startsWith('$.')) {
    return undefined;
  }

  const segments = path.slice(2).split('.').filter(Boolean);
  let current = payload;

  for (const segment of segments) {
    if (current === null || current === undefined) {
      return undefined;
    }

    if (Array.isArray(current)) {
      const index = Number(segment);
      if (Number.isNaN(index)) {
        return undefined;
      }

      current = current[index];

      continue;
    }

    if (isPlainObject(current)) {
      current = current[segment];

      continue;
    }

    return undefined;
  }

  return current;
}

/**
 * Paths relative to one array element (for arrayTargets item mappings).
 */
export function extractArrayItemPaths(payload, arraySourcePath) {
  if (!arraySourcePath || !payload) {
    return [];
  }

  const resolved = resolveJsonPath(payload, arraySourcePath);
  if (resolved === null || resolved === undefined) {
    return [];
  }

  const sampleItem = Array.isArray(resolved) ? resolved[0] : resolved;
  if (sampleItem === null || sampleItem === undefined) {
    return [];
  }

  return extractJsonPaths(sampleItem);
}

/**
 * Array paths on the root payload only (no indexed/nested fields under arrays).
 */
function walkArrayPathsOnly(value, currentPath, paths, seen) {
  if (paths.length >= MAX_PATHS) {
    return;
  }

  if (value === null || value === undefined) {
    return;
  }

  if (Array.isArray(value)) {
    if (!seen.has(currentPath)) {
      seen.add(currentPath);
      paths.push(currentPath);
    }

    return;
  }

  if (isPlainObject(value)) {
    Object.keys(value).forEach((key) => {
      walkArrayPathsOnly(value[key], appendSegment(currentPath, key), paths, seen);
    });
  }
}

export function extractArrayPaths(payload) {
  if (payload === null || payload === undefined) {
    return [];
  }

  const paths = [];
  const seen = new Set();

  if (Array.isArray(payload)) {
    walkArrayPathsOnly(payload, '$', paths, seen);
  } else if (isPlainObject(payload)) {
    Object.keys(payload).forEach((key) => {
      walkArrayPathsOnly(payload[key], appendSegment('$', key), paths, seen);
    });
  }

  return paths.sort((a, b) => a.localeCompare(b));
}

/**
 * Scalar/object paths only — excludes arrays and anything inside them.
 */
function walkScalarPathsOnly(value, currentPath, paths, seen) {
  if (paths.length >= MAX_PATHS) {
    return;
  }

  if (value === null || value === undefined) {
    if (!seen.has(currentPath)) {
      seen.add(currentPath);
      paths.push(currentPath);
    }

    return;
  }

  if (Array.isArray(value)) {
    return;
  }

  if (isPlainObject(value)) {
    if (!seen.has(currentPath)) {
      seen.add(currentPath);
      paths.push(currentPath);
    }

    Object.keys(value).forEach((key) => {
      walkScalarPathsOnly(value[key], appendSegment(currentPath, key), paths, seen);
    });

    return;
  }

  if (!seen.has(currentPath)) {
    seen.add(currentPath);
    paths.push(currentPath);
  }
}

export function extractScalarPaths(payload) {
  if (payload === null || payload === undefined) {
    return [];
  }

  const paths = [];
  const seen = new Set();

  if (Array.isArray(payload)) {
    return [];
  }

  if (isPlainObject(payload)) {
    Object.keys(payload).forEach((key) => {
      walkScalarPathsOnly(payload[key], appendSegment('$', key), paths, seen);
    });
  } else {
    paths.push('$');
  }

  return paths.sort((a, b) => a.localeCompare(b));
}

export function extractJsonPaths(payload) {
  if (payload === null || payload === undefined) {
    return [];
  }

  const paths = [];
  const seen = new Set();

  if (Array.isArray(payload)) {
    walk(payload, '$', paths, seen);
  } else if (isPlainObject(payload)) {
    Object.keys(payload).forEach((key) => {
      walk(payload[key], appendSegment('$', key), paths, seen);
    });
  } else {
    paths.push('$');
  }

  return paths.sort((a, b) => a.localeCompare(b));
}

export function parseJsonSample(text) {
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error('JSON sample is empty');
  }

  const parsed = JSON.parse(trimmed);
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('JSON sample must be an object or array');
  }

  return parsed;
}
